import { useCallback } from "react";
import { dataActions } from "@src/services/state/dataReducer";
import type * as TDT from "@tradingdroid/frontends-types";
import { useDispatch } from "react-redux";

import { useDataState } from "./data";

interface ExecutionActions {
  removeExecution: (executionId: number) => void;
  upsertExecution: (execution: TDT.ApiExecution) => void;
  setExecutions: (newExecutions: TDT.ApiExecution[]) => void;
  upsertPositionInExecution: (
    executionId: number,
    position: Partial<TDT.ApiPosition> & { id: number },
  ) => void;
  addMovementToPosition: (
    executionId: number,
    positionId: number,
    movement: TDT.ApiPositionMovement,
  ) => void;
}

/**
 * Hook for execution actions only (no data subscription - won't cause re-renders)
 * Use this when you only need to dispatch actions, not read data
 */
export const useExecutionActions = (): ExecutionActions => {
  const dispatch = useDispatch();

  const removeExecution = useCallback(
    (executionId: number) => {
      dispatch(dataActions.removeStateExecution(executionId));
    },
    [dispatch],
  );

  const upsertExecution = useCallback(
    (execution: TDT.ApiExecution) => {
      dispatch(dataActions.upsertExecution(execution));
    },
    [dispatch],
  );

  const setExecutions = useCallback(
    (newExecutions: TDT.ApiExecution[]) => {
      dispatch(dataActions.setExecutions(newExecutions));
    },
    [dispatch],
  );

  const upsertPositionInExecution = useCallback(
    (executionId: number, position: Partial<TDT.ApiPosition> & { id: number }) => {
      dispatch(dataActions.upsertPositionInExecution({ executionId, position }));
    },
    [dispatch],
  );

  const addMovementToPosition = useCallback(
    (executionId: number, positionId: number, movement: TDT.ApiPositionMovement) => {
      dispatch(dataActions.addMovementToPosition({ executionId, positionId, movement }));
    },
    [dispatch],
  );

  return {
    removeExecution,
    upsertExecution,
    setExecutions,
    upsertPositionInExecution,
    addMovementToPosition,
  };
};

/**
 * Hook for executions data + actions (subscribes to executions array)
 * Use this when you need to read the executions list
 */
export const useExecutions = () => {
  const executions = useDataState().executions;
  const actions = useExecutionActions();

  return {
    executions,
    ...actions,
  };
};
